/**
 * Per-turn aggregation of file-creating tool calls into a chip render plan.
 *
 * A "turn" is the run of assistant messages between two user messages. Smaller
 * models often split a turn into several assistant messages: tool calls land in
 * one message, the natural-language wrap-up text in another. A naive
 * per-message chip render would attach the chips to the tool-call message —
 * placing them visually *above* the text reply and rendering them while the
 * text is still streaming. Aggregating per-turn lets us pin the chip surface
 * to the last assistant message of the turn, where it sits cleanly under the
 * final text and only appears once streaming has finished.
 *
 * Suppression: when any explicit chip-emitting tool (`complete`,
 * `finish_and_respond`, `render_ui`) or artifact tool (`create_artifact`,
 * `update_artifact`, `generate_image`) appears anywhere in the turn, we skip
 * the auto layer entirely so we don't double up with renderers that already
 * own the chip / preview UI.
 */

import { ARTIFACT_TOOL_NAMES, isDeliverableFile } from "@taqat/core/types";
import type { ChatMessage } from "@/types/chat-message";

/**
 * Tools whose output should render as a file chip pinned to the last
 * assistant message of the turn. The chip's preview/download falls back from
 * the sandbox content endpoint to the cloud `publicUrl` returned by the tool,
 * so cloud-upload-only callers (`pdf_generator`) are valid here too — they
 * also drop a copy into the workspace dir when one is running, which keeps
 * the in-chat preview path working.
 */
const SANDBOX_FILE_WRITE_TOOLS = new Set<string>([
  "write_file",
  "sandbox_create_file",
  "sandbox_write_file",
  "pdf_generator",
  /*
    `sandbox_str_replace` and `sandbox_full_file_rewrite` are deliberately NOT
    here. They modify a file that already exists, which is work, not a
    deliverable — a refactor touching thirty files was rendering thirty
    "attachments" the user never asked for (#1543). A file the agent both
    creates and later edits still chips, via its create call.
  */
  // Tools whose *output* (not input) names a workspace file we should chip.
  // `computer_screenshot` takes no input and returns the saved PNG path in
  // `output.data.filePath`. Listed here so the path resolver in
  // `computeChipRenderPlans` knows to consult the output side too.
  "computer_screenshot",
]);

/**
 * Subset of SANDBOX_FILE_WRITE_TOOLS whose file path lives on the tool
 * *output*, not the input. The resolver in `computeChipRenderPlans` checks
 * this set to decide which side to read FIRST.
 */
const OUTPUT_PATH_TOOLS = new Set<string>([
  "computer_screenshot",
]);

/**
 * The workspace-relative file name a file-writing tool call produced, or
 * `undefined` when it named no file.
 *
 * Both sides are consulted, because which one carries the name depends on the
 * tool *and* on what the model chose to pass:
 *
 * - `write_file` / `sandbox_*` take `path` as a required input — input side.
 * - `computer_screenshot` takes no input at all and reports the saved PNG on
 *   `output.data.filePath` — output side.
 * - `pdf_generator` accepts an *optional* `filename` and generates one
 *   (`<title>-<date>-<uuid>.pdf`) when the model omits it, returning the real
 *   name only on `output.filename`. Reading the input alone meant every
 *   auto-named PDF silently rendered no chip — the file existed, was uploaded,
 *   and was written into the workspace dir, but the user saw nothing to click.
 *
 * `OUTPUT_PATH_TOOLS` decides which side is tried first; the other is a
 * fallback, so a tool that names its file on either side always chips.
 */
function resolveChipPath(
  toolName: string,
  part: { input?: unknown; output?: unknown },
): string | undefined {
  const input = (part.input ?? {}) as Record<string, unknown>;
  const output = (part.output ?? {}) as Record<string, unknown> & {
    data?: { filePath?: unknown };
  };

  const fromInput = input.path ?? input.file_path ?? input.filename;
  // `output.filename` is the name the tool actually wrote into the workspace
  // dir — the same value the preview endpoint resolves against.
  const fromOutput =
    output.data?.filePath ?? output.filePath ?? output.filename ?? output.path;

  const preferred = OUTPUT_PATH_TOOLS.has(toolName)
    ? [fromOutput, fromInput]
    : [fromInput, fromOutput];

  for (const candidate of preferred) {
    if (typeof candidate === "string" && candidate.length > 0) return candidate;
  }
  return undefined;
}

/**
 * Tools that already render their own chip / preview surface. If any of these
 * appears in the turn, the auto layer steps aside.
 */
const SUPPRESSING_TOOLS = new Set<string>([
  "complete",
  "finish_and_respond",
  "render_ui",
  ARTIFACT_TOOL_NAMES.CREATE,
  ARTIFACT_TOOL_NAMES.UPDATE,
  ARTIFACT_TOOL_NAMES.GENERATE_IMAGE,
]);

/**
 * Map from assistant message id to the list of unique file paths that should
 * render as chips at the bottom of that message. Only the *last* assistant
 * message of each turn is ever a key in this map.
 */
export type ChipRenderPlans = Map<string, string[]>;

/**
 * A turn boundary is a *real* user message — one the human actually typed. The
 * AI SDK persists role=user relay messages between assistant turns that mirror
 * the cumulative assistant state (reasoning + text + tool calls + data-status
 * frames). They carry text content, so a "has any text" check isn't enough;
 * rendering them puts the assistant's own reasoning + tool calls inside the
 * user-aligned (right-side) bubble lane and duplicates them across every later
 * relay.
 *
 * Heuristic: a real human-submitted prompt is always finalised before send —
 * it arrives as a complete `text` (or `file`) part, never streamed. A
 * `text-delta` on a role=user message is itself a relay signature. Same for
 * any `reasoning*`, `tool-*`, `data-*`, or `step-*` part. We use an
 * allow-list rather than a deny-list so the filter stays tight even on the
 * partial mid-stream forms (which kept slipping through when only
 * `text-delta` had arrived for a relay).
 */
const REAL_USER_PART_TYPES = new Set<string>(["text", "file"]);

export function isRealUserMessage(message: ChatMessage): boolean {
  if (message.role !== "user") return false;

  let hasNonEmptyText = false;
  for (const part of message.parts) {
    const t = part.type as string;
    if (!REAL_USER_PART_TYPES.has(t)) {
      // Any unexpected part type — text-delta, reasoning, tool-*, data-*,
      // step-start, etc. — marks the message as a synthetic relay.
      return false;
    }
    if (t === "text") {
      const text = (part as { text?: string }).text;
      if (typeof text === "string" && text.trim().length > 0) {
        hasNonEmptyText = true;
      }
    } else if (t === "file") {
      hasNonEmptyText = true;
    }
  }
  return hasNonEmptyText;
}

/**
 * Walk the message list, partition it into turns (by user message boundaries),
 * and produce a per-turn chip plan attached to the final assistant message.
 */
export function computeChipRenderPlans(messages: ChatMessage[]): ChipRenderPlans {
  const plans: ChipRenderPlans = new Map();

  let turnFiles: string[] = [];
  let turnSeen: Set<string> = new Set();
  let turnSuppressed = false;
  let lastAssistantIdInTurn: string | null = null;

  const flushTurn = () => {
    if (
      lastAssistantIdInTurn &&
      !turnSuppressed &&
      turnFiles.length > 0
    ) {
      plans.set(lastAssistantIdInTurn, turnFiles);
    }
    turnFiles = [];
    turnSeen = new Set();
    turnSuppressed = false;
    lastAssistantIdInTurn = null;
  };

  for (const message of messages) {
    if (isRealUserMessage(message)) {
      flushTurn();
      continue;
    }
    if (message.role !== "assistant") continue;

    lastAssistantIdInTurn = message.id;

    for (const part of message.parts) {
      if (!part.type.startsWith("tool-")) continue;
      const toolName = part.type.slice("tool-".length);

      if (SUPPRESSING_TOOLS.has(toolName)) {
        turnSuppressed = true;
        continue;
      }
      if (!SANDBOX_FILE_WRITE_TOOLS.has(toolName)) continue;

      const state = (part as { state?: string }).state;
      if (state !== "output-available") continue;

      const path = resolveChipPath(
        toolName,
        part as { input?: unknown; output?: unknown },
      );
      if (!path) continue;
      /*
        Only deliverables get a chip. A coding task writes source, config and
        lockfiles; chipping those dumped the whole file list into the
        conversation (#1543) and put dotfiles like `.env` one click from anyone
        reading the chat. `isDeliverableFile` is the same predicate the Library
        uses, so the two surfaces can't disagree about what a document is.
      */
      if (!isDeliverableFile(path)) continue;
      if (turnSeen.has(path)) continue;
      turnSeen.add(path);
      turnFiles.push(path);
    }
  }

  // Trailing turn (no closing user message — common while the agent is mid-reply).
  flushTurn();

  return plans;
}
